import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  AlertTriangle,
  Box,
  CheckCircle2,
  Loader2,
  Package,
  PackagePlus,
  RefreshCw,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { ProgressBar } from '../../../../components/ui/ProgressBar';
import { CandidatesSheet } from '../../components/CandidatesSheet';
import { CodeChoice } from '../../components/CodeChoice';
import { ConfirmSheet } from '../../components/ConfirmSheet';
import { OperatorActionBar } from '../../components/OperatorActionBar';
import { OperatorScanPanel } from '../../components/OperatorScanPanel';
import { TaskTimeChip } from '../../components/TaskTimeChip';
import { PackingGates } from './PackingGates';
import { PackingRelabel } from './PackingRelabel';
import { PackingResult } from './PackingResult';
import { taskEntered } from '../../../../features/operator/operatorDeviceSlice';
import { OPERATOR_FLOW } from '../../../../features/operator/operatorConstants';
import {
  addAnotherItem,
  addItem,
  choosePackingLine,
  fetchPackingOrder,
  loadPackingOrder,
  removeItem,
  removeItemSupply,
  repeatLastPackedProduct,
  retryLastPackingWrite,
  saveItem,
  scanPackingCode,
  sendToCourier,
  submitSendToCourier,
} from '../../../../features/operatorPacking/operatorPackingThunks';
import { packingResolutionDismissed } from '../../../../features/operatorPacking/operatorPackingActions';
import {
  packingEntered,
  packingItemFocused,
  packingLeft,
  selectPackingAccepting,
} from '../../../../features/operatorPacking/operatorPackingSlice';
import {
  canSendPacking,
  changedMeasures,
  currentItemOf,
  isOrderPacked,
  isSendingToCourier,
  itemRemovalText,
  itemUnweighedNote,
  itemWeightSuggestionText,
  leaveWithoutLabelSheet,
  leavesWithoutLabel,
  measuredItemsText,
  mergeMeasureValues,
  packingBusyLabel,
  packingNextStep,
  packingProgress,
  packingTapKey,
  packingWriteKey,
  selectPackingCourierSend,
  selectPackingGates,
  selectPackingNext,
  supplyOptionsOf,
} from '../../../../features/operatorPacking/operatorPackingUtils';
import { LOGISTICS_FIELDS, packageTareGrams } from '../../../../features/fulfillment/fulfillmentUtils';
import { selectIsRunning } from '../../../../features/feedback/feedbackSlice';
import { unitCode } from '../../../../features/operator/operatorCodes';
import { SCAN_SOURCE } from '../../../../features/operator/operatorScanGate';
import { orderCardTitle, returnToOf, selectCourierRelabelReady } from '../../../../features/operator/operatorUtils';
import { isContainer } from '../../../../features/supplies/suppliesUtils';
import { formatQuantity } from '../../../../utils/inventoryLabels';

/**
 * A supply or a product the screen offers to tap: it reads as scanning it
 * would, into the item on screen, and says so while that runs.
 */
const PackingTap = ({ read, ...props }) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectIsRunning(scanPackingCode, packingTapKey(read)));
  return (
    <CodeChoice
      {...props}
      loading={loading}
      onTap={(code) => dispatch(scanPackingCode({ code, source: SCAN_SOURCE.TAP, ...read }))}
    />
  );
};

const valuesOf = (item) =>
  Object.fromEntries(LOGISTICS_FIELDS.map(({ key }) => [key, item[key] == null ? '' : String(item[key])]));

/**
 * An item's weight and measures. Seeded from what the server has; when that
 * changes, the fields it moved show it at once — a box lending its measures,
 * the weight following the suggestion — and what is typed in the others
 * stays. Saved together: only what changed, and a field emptied clears it. A
 * value that is not a whole number keeps the button off, saying what to fix.
 * «Usar N g» takes the suggestion back once the weight was typed by hand.
 */
const MeasuresForm = ({ orderId, item, locked }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState(() => valuesOf(item));
  const seen = useRef(values);
  const { changed, problem } = changedMeasures(item, values);
  const tare = packageTareGrams(item);
  const suggestion = itemWeightSuggestionText(item);
  const unweighed = itemUnweighedNote(item);
  const suggestedTotal = item.weight_suggestion?.total || 0;
  const offerSuggestion = suggestedTotal > 0 && Number(values.weight_grams || 0) !== suggestedTotal;

  useEffect(() => {
    const before = seen.current;
    const after = valuesOf(item);
    seen.current = after;
    if (LOGISTICS_FIELDS.every(({ key }) => before[key] === after[key])) return;
    setValues((typed) => mergeMeasureValues(before, after, typed));
  }, [item]);

  const takeSuggestion = () => {
    setValues((prev) => ({ ...prev, weight_grams: String(suggestedTotal) }));
    dispatch(saveItem({ orderId, itemId: item.id, values: { weight_grams: suggestedTotal } }));
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {LOGISTICS_FIELDS.map(({ key, label, unit }) => (
          <label key={key} className="block">
            <span className="mb-1 block text-xs font-semibold text-text-secondary">
              {label} ({unit})
            </span>
            <input
              data-testid={`item-${key}`}
              value={values[key]}
              onChange={(event) => setValues((prev) => ({ ...prev, [key]: event.target.value }))}
              inputMode="numeric"
              pattern="[0-9]*"
              enterKeyHint="done"
              autoComplete="off"
              className="h-12 w-full rounded-xl border border-border bg-ninja-gray px-3 text-lg font-semibold text-ninja-dark outline-none focus:border-ninja-green dark:bg-[#111] dark:text-white"
            />
          </label>
        ))}
      </div>
      {(tare > 0 || suggestion) && (
        <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-text-secondary">
          {tare > 0 && <span>Incluye {formatQuantity(tare)} g de caja.</span>}
          {suggestion && <span data-testid="item-weight-suggestion">{suggestion}</span>}
        </p>
      )}
      {unweighed && (
        <p
          data-testid="item-unweighed"
          className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300"
        >
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          {unweighed}
        </p>
      )}
      {offerSuggestion && (
        <PendingButton
          pendingOn={saveItem}
          pendingKey={item.id}
          variant="ghost"
          size="lg"
          fullWidth
          disabled={locked}
          loadingLabel="Guardando…"
          onClick={takeSuggestion}
          data-testid="item-weight-use"
        >
          Usar {formatQuantity(suggestedTotal)} g
        </PendingButton>
      )}
      <PendingButton
        pendingOn={saveItem}
        pendingKey={item.id}
        variant="secondary"
        size="lg"
        fullWidth
        disabled={!changed || Boolean(problem) || locked}
        loadingLabel="Guardando…"
        onClick={() => dispatch(saveItem({ orderId, itemId: item.id, values: changed }))}
        data-testid="item-save-measures"
      >
        {problem || 'Guardar medidas'}
      </PendingButton>
    </div>
  );
};

/** The ✕ of a supply in an item: takes it out and back to its stock. */
const RemoveSupplyButton = ({ orderId, supply, locked }) => {
  const dispatch = useDispatch();
  return (
    <PendingButton
      pendingOn={removeItemSupply}
      pendingKey={supply.id}
      variant="subtle"
      size="lg"
      iconOnly
      icon={X}
      pill
      disabled={locked}
      aria-label={`Quitar ${supply.sku}`}
      onClick={() => dispatch(removeItemSupply({ orderId, supplyUseId: supply.id }))}
      data-testid="item-supply-remove"
      data-supply-use-id={supply.id}
    />
  );
};

const PackingItemCard = ({ orderId, item, current, onlyOne, locked, onFocus, onRemove }) => {
  return (
    <li
      data-testid="item-card"
      data-item-id={item.id}
      data-complete={String(Boolean(item.measured))}
      data-current={String(current)}
      className={clsx(
        'space-y-3 rounded-3xl border-2 bg-white p-4 dark:bg-[#1a1a1a]',
        current ? 'border-ninja-green' : 'border-border'
      )}
    >
      <div className="flex items-center justify-between gap-3">
        {/* flex-1 rather than its full width, so «Quitar bulto» beside it keeps its 48 px. */}
        <Button variant="choice" onClick={onFocus} className="flex-1">
          <span className="flex items-center gap-2">
            <span className="text-lg font-bold text-ninja-dark dark:text-white">Bulto {item.item_index}</span>
            {item.measured && <CheckCircle2 size={20} className="text-ninja-green" />}
            {!current && <span className="text-xs text-text-secondary">· tocar para usarlo</span>}
          </span>
        </Button>
        {!onlyOne && item.supplies.length === 0 && (
          <Button
            variant="subtle"
            size="lg"
            iconOnly
            icon={Trash2}
            disabled={locked}
            aria-label={`Quitar bulto ${item.item_index}`}
            onClick={onRemove}
            data-testid="item-remove"
            data-item-id={item.id}
          />
        )}
      </div>

      {item.supplies.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {item.supplies.map((supply) => (
            <span key={supply.id} data-testid="item-supply" className="flex items-center gap-1 rounded-full border border-border pl-3 text-sm font-semibold text-ninja-dark dark:text-white">
              {supply.sku}
              {supply.quantity > 1 ? ` ×${supply.quantity}` : ''}
              <RemoveSupplyButton orderId={orderId} supply={supply} locked={locked} />
            </span>
          ))}
        </div>
      )}

      {item.products.length > 0 ? (
        <ul className="space-y-1 text-sm">
          {item.products.map((product) => (
            <li key={product.line_id} className="flex justify-between gap-3 text-ninja-dark dark:text-white">
              <span className="truncate">{product.sku}</span>
              <span className="font-semibold">×{formatQuantity(product.quantity)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-text-secondary">Sin productos todavía.</p>
      )}

      <MeasuresForm orderId={orderId} item={item} locked={locked} />
    </li>
  );
};

/**
 * A write of this order left unconfirmed: no answer, or one worth sending
 * again. Nothing else of the order goes until it is sent again and the server
 * says whether it had landed. Reloading shows how the order is, but settles
 * nothing: the lost write may still land after the reload. The band can be
 * closed, and the order left and opened again; this stays.
 */
const UnconfirmedWrite = ({ orderId, unconfirmed, loading, pending, onRetry, onReload }) => (
  <section
    data-testid="packing-unconfirmed"
    role="alert"
    className="space-y-3 rounded-3xl border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300"
  >
    <div className="flex items-start gap-3">
      <AlertTriangle size={22} className="mt-0.5 shrink-0" />
      <div className="space-y-1">
        <p className="text-base font-bold">La última acción no se confirmó</p>
        <p className="text-sm">
          Reintenta antes de seguir: si ya había llegado, no se registra dos veces. Recargar la orden muestra cómo va, pero
          no la confirma.
        </p>
      </div>
    </div>
    <div className="grid gap-2">
      <PendingButton
        pendingOn={unconfirmed.type}
        pendingKey={packingWriteKey(unconfirmed.arg)}
        variant="primary"
        size="lg"
        fullWidth
        icon={RotateCcw}
        disabled={loading}
        loadingLabel="Reintentando…"
        onClick={onRetry}
        data-testid="packing-retry"
      >
        Reintentar
      </PendingButton>
      <PendingButton
        pendingOn={fetchPackingOrder}
        pendingKey={orderId}
        variant="secondary"
        size="lg"
        fullWidth
        icon={RefreshCw}
        disabled={Boolean(pending)}
        loadingLabel="Recargando…"
        onClick={onReload}
        data-testid="packing-reload"
      >
        Recargar la orden
      </PendingButton>
    </div>
  </section>
);

/**
 * One order, packed on the floor: scan a box (it fills the item's measures)
 * and the products into the item, weigh and measure it. The order closes once
 * it has an item, every item is measured and nothing picked is left outside
 * them (its gates); then the result shows its labels and the primary goes to
 * the next order. The items stay editable below: taking something out reopens
 * it. Once the courier labeled the order, adding or taking out an item leaves
 * its label for other items: the shipment is created again when the order
 * closes (`PackingRelabel`). The floor calls an item a «bulto».
 *
 * Opened from Despacho or Consulta to make an order's label, the primary goes
 * back there. Leaving an order with no label, while the floor can still ask
 * its courier for one, asks first.
 */
export const PackingOrder = () => {
  const { orderId } = useParams();
  const id = Number(orderId);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const packing = useSelector((state) => state.operatorPacking);
  const accepting = useSelector(selectPackingAccepting);
  const gates = useSelector(selectPackingGates);
  const next = useSelector(selectPackingNext);
  const relabels = useSelector(selectCourierRelabelReady);
  const courier = useSelector(selectPackingCourierSend);
  const sending = useSelector(selectIsRunning(sendToCourier, id));
  const { order, loading, error, currentItemId, resolution, pendingWrite, lastProduct } = packing;
  const pending = pendingWrite?.kind ?? null;
  const unconfirmed = packing.unconfirmed[id] ?? null;
  const [removing, setRemoving] = useState(null);
  /** Whether the sheet asks before the primary leaves the order with no label. */
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    dispatch(packingEntered(id));
    dispatch(taskEntered({ flow: OPERATOR_FLOW.PACKING, taskId: id }));
    dispatch(loadPackingOrder(id));
    return () => {
      dispatch(packingLeft());
    };
  }, [dispatch, id]);

  if (!order && loading) {
    return (
      <div className="flex justify-center py-16" data-testid="packing-loading">
        <Loader2 size={32} className="animate-spin text-ninja-green" />
      </div>
    );
  }
  if (!order && error) {
    return (
      <ErrorState title="No pudimos abrir la orden" description={error.message} onRetry={() => dispatch(loadPackingOrder(id))}>
        <Button variant="ghost" onClick={() => navigate('/packing')}>
          Volver
        </Button>
      </ErrorState>
    );
  }
  if (!order) return null;

  const progress = packingProgress(order);
  const current = currentItemOf(order, currentItemId);
  const packed = isOrderPacked(order);
  const toPack = order.lines.filter((line) => Number(line.to_pack) > 0);
  const supplies = supplyOptionsOf(order);
  // One packing action at a time, and none while a lost one waits to go again.
  const locked = !canSendPacking(packing);
  // A write left unconfirmed may still change the order: it is not shown closed, and «Reintentar» stays the primary.
  const done = Boolean(gates?.ready) && !unconfirmed;
  const nextStep = packingNextStep(next, returnToOf(location.state));
  const withoutLabel = leavesWithoutLabel(courier);
  const leaveSheet = leaveWithoutLabelSheet(order);

  // Closed whatever the answer: the band says it, and a lost one leaves the notice.
  const confirmRemove = async () => {
    await dispatch(removeItem({ orderId: id, itemId: removing.id }));
    setRemoving(null);
  };

  const goOn = () => (withoutLabel ? setLeaving(true) : navigate(nextStep.to));

  // Sent from the sheet, the order stays on screen: its label is waited for and printed here.
  const sendBeforeLeaving = async () => {
    await dispatch(submitSendToCourier({ orderId: id }));
    setLeaving(false);
  };

  const leaveWithoutLabel = () => {
    setLeaving(false);
    navigate(nextStep.to);
  };

  return (
    <div className="space-y-5" data-testid="packing-order" data-packed={String(packed)}>
      <section className="space-y-3 rounded-3xl border border-border bg-white p-4 dark:bg-[#1a1a1a]">
        <div>
          <p className="text-xl font-bold text-ninja-dark dark:text-white">{orderCardTitle(order)}</p>
          <p className="text-sm text-text-secondary">{[order.recipient, order.company_name].filter(Boolean).join(' · ')}</p>
        </div>
        {!done && <TaskTimeChip task={order} />}
        <ProgressBar
          testId="packing-progress"
          value={progress.packed}
          max={progress.picked}
          label={`${formatQuantity(progress.packed)} de ${formatQuantity(progress.picked)} un en bultos · ${measuredItemsText(order.items_measured, order.items_count)}`}
        />
      </section>

      {error && (
        <div data-testid="packing-reload-error" role="alert">
          <ErrorState variant="inline" title="No pudimos recargar la orden; puede no estar al día." description={error.message}>
            <PendingButton
              pendingOn={fetchPackingOrder}
              pendingKey={id}
              variant="secondary"
              size="lg"
              fullWidth
              icon={RefreshCw}
              loadingLabel="Recargando…"
              onClick={() => dispatch(loadPackingOrder(id))}
              data-testid="packing-reload-error-retry"
            >
              Recargar la orden
            </PendingButton>
          </ErrorState>
        </div>
      )}

      {unconfirmed && (
        <UnconfirmedWrite
          orderId={id}
          unconfirmed={unconfirmed}
          loading={loading}
          pending={pending}
          onRetry={() => dispatch(retryLastPackingWrite())}
          onReload={() => dispatch(loadPackingOrder(id))}
        />
      )}

      <OperatorScanPanel
        prompt={current ? `Escanea para el bulto ${current.item_index}` : 'Escanea la caja o un producto para empezar'}
        hint={
          done
            ? 'Cada producto suma uno: aparta la cámara entre unidades.'
            : 'Cada producto suma uno: aparta la cámara entre unidades. O tócalos abajo.'
        }
        accepting={accepting}
        lastCode={packing.scan.lastCode}
        // Held behind a write left unconfirmed, a read would go in unseen once it settles; the courier's
        // labels may take a minute and a half, and a read held that long would be a surprise.
        holdReads={!unconfirmed && !isSendingToCourier(packing)}
        busyLabel={packingBusyLabel(packing)}
        onCode={(code, source, alternatives, read) =>
          dispatch(scanPackingCode({ code, source, alternatives, again: Boolean(read?.again) }))
        }
        repeatLabel={
          lastProduct && current
            ? `+${formatQuantity(lastProduct.factor || 1)} ${lastProduct.sku || lastProduct.productCode}`
            : undefined
        }
        onRepeat={() => dispatch(repeatLastPackedProduct())}
      />

      {!done && supplies.length > 0 && (
        <section data-testid="packing-supplies" className="space-y-2">
          <div>
            <h2 className="text-sm font-semibold text-ninja-dark dark:text-white">Cajas e insumos</h2>
            <p className="text-xs text-text-secondary">
              {current ? `Toca una para ponerla en el bulto ${current.item_index}.` : 'Toca una para empezar el bulto 1.'}
            </p>
          </div>
          <ul className="grid grid-cols-2 gap-2">
            {supplies.map((supply) => (
              <li key={supply.id}>
                <PackingTap
                  read={{ supplyId: supply.id }}
                  code={supply.code}
                  label={supply.sku}
                  detail={supply.out ? 'Sin stock' : supply.detail}
                  icon={isContainer(supply) ? Box : Package}
                  disabled={!accepting || supply.out}
                  testId="packing-supply-option"
                  data-supply-id={supply.id}
                  data-kind={supply.kind}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {toPack.length > 0 && (
        <section data-testid="packing-to-pack" className="space-y-2">
          <div>
            <h2 className="text-sm font-semibold text-ninja-dark dark:text-white">Falta poner en un bulto</h2>
            <p className="text-xs text-text-secondary">
              {current
                ? `Toca uno para poner 1 un en el bulto ${current.item_index}.`
                : 'Toca uno para poner 1 un en el bulto 1.'}
            </p>
          </div>
          <ul className="space-y-2">
            {toPack.map((line) => (
              <li key={line.line_id}>
                <PackingTap
                  read={{ lineId: line.line_id }}
                  code={unitCode(line.codes, line.sku)}
                  label={line.sku}
                  detail={line.name}
                  aside={`${formatQuantity(line.in_items)} / ${formatQuantity(line.picked)}`}
                  disabled={!accepting}
                  testId="packing-to-pack-line"
                  data-line-id={line.line_id}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {done ? (
        <PackingResult order={order} />
      ) : (
        <>
          <PackingGates />
          <PackingRelabel order={order} standalone />
        </>
      )}

      <ul className="space-y-3">
        {order.items.map((item) => (
          <PackingItemCard
            key={item.id}
            orderId={id}
            item={item}
            current={current?.id === item.id}
            onlyOne={order.items.length === 1}
            locked={locked}
            onFocus={() => dispatch(packingItemFocused(item.id))}
            onRemove={() => setRemoving(item)}
          />
        ))}
      </ul>

      <OperatorActionBar>
        <PendingButton
          pendingOn={addItem}
          pendingKey={id}
          variant="secondary"
          size="lg"
          icon={PackagePlus}
          disabled={locked}
          loadingLabel="Agregando…"
          onClick={() => dispatch(addAnotherItem())}
          data-testid="item-add"
        >
          Otro bulto
        </PendingButton>
        {done && (
          <Button
            variant="primary"
            size="lg"
            onClick={goOn}
            data-testid="packing-next-order"
            data-order-id={nextStep.orderId ?? undefined}
          >
            {nextStep.label}
          </Button>
        )}
      </OperatorActionBar>

      <ConfirmSheet
        isOpen={leaving && done && withoutLabel}
        title={leaveSheet.title}
        message={leaveSheet.message}
        confirmLabel={leaveSheet.confirmLabel}
        busy={sending}
        busyLabel="Enviando…"
        onConfirm={sendBeforeLeaving}
        onClose={() => setLeaving(false)}
        testId="packing-leave-sheet"
        confirmTestId="packing-leave-send"
      >
        <p className="text-center text-sm text-text-secondary">{leaveSheet.sendText}</p>
        {leaveSheet.warning && (
          <p
            data-testid="packing-leave-unweighed"
            className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300"
          >
            <AlertTriangle size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
            {leaveSheet.warning}
          </p>
        )}
        <Button
          variant="outline"
          size="lg"
          fullWidth
          disabled={sending}
          onClick={leaveWithoutLabel}
          data-testid="packing-leave-skip"
        >
          Seguir sin etiqueta
        </Button>
      </ConfirmSheet>

      <ConfirmSheet
        isOpen={Boolean(removing)}
        title={`¿Quitar el bulto ${removing?.item_index ?? ''}?`}
        message={itemRemovalText(order, { relabels })}
        confirmLabel="Quitar bulto"
        confirmVariant="danger"
        busy={pending === 'removeItem'}
        busyLabel="Quitando…"
        onConfirm={confirmRemove}
        onClose={() => setRemoving(null)}
        testId="remove-item-sheet"
      />

      <CandidatesSheet
        isOpen={resolution?.kind === 'ambiguous'}
        message={resolution?.message}
        options={resolution?.options}
        onChoose={(lineId) => dispatch(choosePackingLine(lineId))}
        onClose={() => dispatch(packingResolutionDismissed())}
        busy={Boolean(pending)}
      />
    </div>
  );
};
